import { trackInternalEvent, trackMarketingEvent } from '@rio/tracking'
import { ready } from 'shared/ready'

const ALLOWED_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]
const MAX_SIZE = 10 * 1024 * 1024 // 10MB in bytes
const FLOW = '3-screen'

class UploadModalSettings {
  constructor() {
    this.fileData = undefined

    this.$modalUploadContainer = document.getElementById('upload-modal-container')
    // just to confirm that the v1 is the one being used.
    if (!this.$modalUploadContainer) return

    this.$modalUploadRetry = document.getElementById('modal_upload__button-retry')
    this.$formConfirmEmail = document.getElementById('modal_upload__confirm-email_form')

    /* MODAL STEPS */
    this.$stepUploadFailed = document.getElementById('upload-failed-container')
    this.$stepAddEmail = document.getElementById('upload-completed-container')
    this.$stepUploadData = document.getElementById('upload_send__api-container')

    this.fillEventListeners()
    this.fillHomepageEventListeners()
  }

  openModal() {
    this.$modalUploadContainer.classList.add('is-animated', 'is-visible')
  }

  closeModal() {
    this.$modalUploadContainer.classList.remove('is-animated', 'is-visible')
    this.$stepAddEmail.classList.add('hidden')
    this.$stepUploadData.classList.add('hidden')
    this.$stepUploadFailed.classList.add('hidden')
  }

  checkFile(file) {
    if (!file) return { success: false, message: 'no file' }
    if (!ALLOWED_TYPES.includes(file.type))
      return { success: false, message: 'file not valid type' }
    if (file.size > MAX_SIZE) return { success: false, message: 'file over 10mb' }

    return { success: true }
  }

  resetUploadModalSettings() {
    const screen = this.visibleScreen()
    trackInternalEvent('click_exit_upload_modal', {
      ...this.getEventParams(),
      screen,
    })
    this.fileData = undefined
    this.closeModal()
  }

  async sendUploadDetails(e) {
    e.preventDefault()
    const email = e.target.email.value || undefined

    // Clear any previous error messages
    this.clearEmailError()

    try {
      // call the api to create the email
      const createUserResponse = await this.createUser(email)

      // If user already exists, show a message with a link to sign-in page
      if (
        !createUserResponse.success &&
        createUserResponse.error === 'User already exists' &&
        (createUserResponse.userExists || createUserResponse.user_exists)
      ) {
        this.showEmailExistsMessage(createUserResponse.signInUrl || createUserResponse.sign_in_url)
        return false
      }

      // If other error, show error modal
      if (!createUserResponse.success) {
        this.prepareErrorModal()
        return false
      }

      trackMarketingEvent('Sign up', 'Upload CTA', { flow: FLOW })
      // move to upload modal
      this.showLastStep()

      // call the api to upload
      const uploadResponse = await this.uploadCV(email)
      if (!uploadResponse) return false

      // all good call the api to upload the data associated to the email.
      window.location.replace(uploadResponse)
    } catch (error) {
      this.prepareErrorModal()
      return false
    }
  }

  async directUpload() {
    try {
      this.showLastStep()

      const uploadResponse = await this.uploadCV()
      if (!uploadResponse) {
        console.error('Upload failed, showing error modal')
        setTimeout(() => {
          this.prepareErrorModal()
        }, 100)
        return false
      }

      window.location.replace(uploadResponse)
    } catch (error) {
      console.error('Direct upload error:', error)

      setTimeout(() => {
        this.prepareErrorModal()
      }, 100)
      return false
    }
  }

  /* API calls */
  async createUser(email) {
    const response = await fetch('/api/builder/resume_upload/register', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: email,
      }),
    })

    const data = await response.json()
    return {
      success: response.status === 200,
      ...data,
    }
  }

  async uploadCV(email = null) {
    const form = new FormData()
    form.append('resume', this.fileData)

    // If email is provided, we're creating a new user
    // Otherwise upload directly
    if (email) {
      form.append('email', email)
    }

    const eventParams = this.getEventParams()

    const params = new URLSearchParams()
    if (eventParams.template) {
      params.set('template', eventParams.template)
    }

    trackInternalEvent('submit_sign_up_upload', { ...eventParams, flow: FLOW })
    const query = params.toString()

    const response = await fetch(`/api/builder/resume_upload/upload_and_parse?${query}`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
      body: form,
    })

    if (response.status !== 200) return false
    const data = await response.json()

    return data.redirectUrl
  }

  /* STEP Selection */
  prepareErrorModal() {
    this.$stepUploadFailed.classList.add('hidden')
    this.$stepAddEmail.classList.add('hidden')
    this.$stepUploadData.classList.add('hidden')

    this.$stepUploadFailed.classList.remove('hidden')
  }

  continueUploadSteps() {
    this.$stepUploadFailed.classList.add('hidden')
    this.$stepAddEmail.classList.add('hidden')
    this.$stepUploadData.classList.add('hidden')

    this.$stepAddEmail.classList.remove('hidden')
  }

  showLastStep() {
    this.$stepUploadFailed.classList.add('hidden')
    this.$stepAddEmail.classList.add('hidden')
    this.$stepUploadData.classList.add('hidden')

    this.$stepUploadData.classList.remove('hidden')
  }

  /* Prepare event listeners */
  fillEventListeners() {
    document
      .getElementById('modal__close-upload')
      .addEventListener('click', this.resetUploadModalSettings.bind(this))
    this.$formConfirmEmail &&
      this.$formConfirmEmail.addEventListener('submit', this.sendUploadDetails.bind(this))

    const modalInput = document.getElementById('resumeUploadFileInput')
    document.getElementById('modal_upload__button-retry').addEventListener('click', e => {
      e.preventDefault()
      modalInput.click()
    })
  }

  visibleScreen() {
    if (!Array.from(this.$stepUploadFailed.classList).includes('hidden'))
      return { screen: 'upload_failed' }
    if (!Array.from(this.$stepAddEmail.classList).includes('hidden')) return { screen: 'email' }

    return { screen: 'uploading' }
  }

  getEventParams() {
    const { page_id: label, block, template, position } = this.$modalUploadContainer.dataset || {}

    const eventParams = {
      label,
      block,
    }
    if (template) {
      eventParams.template = template
      eventParams.original_position = position
    }

    return eventParams
  }

  fillHomepageEventListeners() {
    let global = this

    const input = document.getElementById('resumeUploadFileInput')
    const uploadButtons = document.querySelectorAll('.upload__resume-button')

    uploadButtons.forEach(button => {
      button.addEventListener('click', e => {
        e.preventDefault()
        const dataset = this.$modalUploadContainer.dataset
        const redirectUrl = dataset.redirectUrl
        if (redirectUrl && !button.dataset.noredirect) {
          window.location.href = redirectUrl
          return
        }

        input.click()

        const params = Object.entries(button.dataset)
        params.forEach(([key, value]) => {
          this.$modalUploadContainer.dataset[key] = value
        })
        trackInternalEvent('click_upload_resume_website', {
          ...this.getEventParams(),
          flow: FLOW,
        })
      })
    })

    input.addEventListener('change', e => {
      e.preventDefault()
      // check the file type and size if they are ok
      const file = e.target.files[0] || undefined

      let fileUploadChecker = this.checkFile(file)
      if (fileUploadChecker.success) {
        this.fileData = file
        trackInternalEvent('upload_resume_success', { ...this.getEventParams(), flow: FLOW })

        // Check if we can upload directly (user is logged in and can create resume)
        const canUploadDirectly = this.$modalUploadContainer.dataset.canUploadDirectly === 'true'

        if (canUploadDirectly) {
          // For logged-in users who can create resumes, skip the email step
          global.openModal()
          this.directUpload()
        } else {
          // For guest users, show the email step
          this.continueUploadSteps()
          global.openModal()
        }
      } else {
        trackInternalEvent('upload_resume_error', {
          ...this.getEventParams(),
          error: fileUploadChecker.message || '',
        })
        this.prepareErrorModal()
        global.openModal()
      }
    })

    const uploadAreas = document.querySelectorAll('.ats-checker-upload-container')

    uploadAreas.forEach(area => {
      area.addEventListener('dragover', e => {
        e.preventDefault()
        area.classList.add('dragover')
      })

      area.addEventListener('dragleave', () => {
        area.classList.remove('dragover')
      })

      area.addEventListener('drop', e => {
        e.preventDefault()
        area.classList.remove('dragover')

        const file = e.dataTransfer.files[0]
        if (!file) return

        const fileUploadChecker = this.checkFile(file)
        if (fileUploadChecker.success) {
          this.fileData = file

          trackInternalEvent('upload_resume_success', this.getEventParams())
          this.continueUploadSteps()
        } else {
          trackInternalEvent('upload_resume_error', {
            ...this.getEventParams(),
            error: fileUploadChecker.message || '',
          })
          this.prepareErrorModal()
        }

        global.openModal()
      })
    })
  }

  clearEmailError() {
    // Remove any existing error message
    const errorContainer = document.querySelector('.upload-modal__email-error')
    if (errorContainer) {
      errorContainer.style.display = 'none'
    }
  }

  showEmailExistsMessage(signInUrl) {
    this.clearEmailError()

    this.$stepUploadFailed.classList.add('hidden')
    this.$stepUploadData.classList.add('hidden')
    this.$stepAddEmail.classList.remove('hidden')

    // Get the email form and input
    const emailForm = document.getElementById('modal_upload__confirm-email_form')
    const emailInput = emailForm.querySelector('input[name="email"]')
    const errorContainer = emailForm.querySelector('.upload-modal__email-error')

    emailInput.style.border = '1px solid #dc3545'
    emailInput.style.borderRadius = '4px'

    // Show the error container
    errorContainer.style.display = 'block'
  }
}

ready(function () {
  new UploadModalSettings()
})
